import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { ApiUsageAggregates, ApiUsageRequest } from '../../types';
import { formatDateTimeUTC } from '../../utils/dateTime';
import ConvertedTokensChart from './ConvertedTokensChart';
import UsageBreakdownChart from './UsageBreakdownChart';
import ApiKeyFilter, { API_SCOPE } from '../filters/ApiKeyFilter';
import DateRangeFilter from '../filters/DateRangeFilter';
import { idsParam } from '../filters/EntityFilter';
import { EmptyRow, SectionFilterBar, SortableTh, TableCard, TableHeader, TableViewport, usePageSize, tdClass } from '../table/TableKit';
import { StatTile } from '../cards/StatCards';
import { Badge } from '../StatusBadge';
import { ErrorNotice } from '../ErrorNotice';
import RefreshButton from '../RefreshButton';
import { convertedTokensByDay, KindUsageRow, TokenKind } from './usageBreakdown';
import { usePageClamp } from '../../hooks/usePageClamp';
import { useUsageRange } from '../../hooks/useUsageRange';
import { urlEnum, urlIdList, urlIsoDate, urlPositiveInt, useUrlState } from '../../hooks/useUrlState';
import { fromHere } from '../../hooks/useBackTo';
import { apiKeyName, maskApiKey } from '../../utils/apiKey';
import { formatCentsAsDollars } from '../../utils/money';

// Fixed height so every control in the filter bar lines up.
const filterClass =
    'h-10 bg-body-bg border border-gray-600 text-body-color focus:border-primary focus:outline-none rounded-md pl-3 pr-8 py-2 select-chevron';

const emptyAggregates: ApiUsageAggregates = {
    unique_count: 0,
    duplicate_count: 0,
    input_tokens_sum: 0,
    input_tokens_converted_sum: 0,
    input_tokens_supported_sum: 0,
    output_tokens_sum: 0,
    cost_cents: null,
    estimated_cost_cents: null,
    total_requests: 0,
    successful: 0,
    failed: 0,
};

// The API only converts, so supported tokens are always 0. Users never see output tokens.
const CHART_TOKEN_KINDS: TokenKind[] = ['converted', 'input'];

const URL_SPEC = {
    keys: urlIdList(),
    status: urlEnum(['', 'success', 'error'] as const, ''),
    range: urlEnum(['7d', '30d', 'custom'] as const, '30d'),
    from: urlIsoDate(),
    to: urlIsoDate(),
    sort: urlEnum(['api_key', 'status', 'input_tokens', 'input_tokens_converted', 'cost', 'completed_at'] as const, 'completed_at'),
    order: urlEnum(['asc', 'desc'] as const, 'desc'),
    page: urlPositiveInt(),
};

function UsageTab({ urlPrefix }: { urlPrefix?: string }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [pageSize, setPageSize] = usePageSize('api-usage');
    const [state, setUrlState] = useUrlState(URL_SPEC, urlPrefix);
    const offset = (state.page - 1) * pageSize;
    const { rangeFilter, range } = useUsageRange(state.range, state.from, state.to);
    const apiKeyIdsParam = idsParam(state.keys);

    const params = useMemo(
        () => ({
            api_key_ids: apiKeyIdsParam,
            status: state.status || undefined,
            from: range?.from,
            to: range?.to,
            limit: pageSize,
            offset,
            sort: state.sort,
            order: state.order,
        }),
        [apiKeyIdsParam, state.status, range, pageSize, offset, state.sort, state.order],
    );

    const { data: fetched, isFetching, isLoading, isPlaceholderData, error, refetch } = useQuery({
        queryKey: ['converter', 'apiUsage', params],
        queryFn: async () => (await apiService.converterGetUsage(params)).data,
        placeholderData: (prev) => prev,
        retry: false,
        enabled: range !== null,
        refetchOnWindowFocus: true,
    });
    // An incomplete custom range fetches nothing. Drop the old numbers: kept on
    // screen they would read as this range's.
    const data = range ? fetched : undefined;

    const dailyParams = useMemo(
        () => ({
            api_key_ids: apiKeyIdsParam,
            status: state.status || undefined,
            from: range?.from,
            to: range?.to,
        }),
        [apiKeyIdsParam, state.status, range],
    );

    const { data: daily, error: dailyError, isFetching: isDailyFetching, refetch: refetchDaily } = useQuery({
        queryKey: ['converter', 'apiUsageDaily', dailyParams],
        queryFn: async () => (await apiService.converterGetUsageDaily(dailyParams)).data,
        retry: false,
        enabled: range !== null,
        refetchOnWindowFocus: true,
    });

    const convertedRows = useMemo(() => range ? convertedTokensByDay(daily?.days ?? [], range) : [], [daily?.days, range]);

    const chartRows: KindUsageRow[] = useMemo(
        () =>
            (daily?.days ?? []).map((day) => ({
                date: day.date,
                keyId: day.api_key_id,
                keyLabel: day.name || maskApiKey(day),
                tokensByKind: { input: day.input_tokens, converted: day.input_tokens_converted, supported: day.input_tokens_supported, output: 0 },
                count: day.requests,
            })),
        [daily],
    );

    const requests: ApiUsageRequest[] = data?.requests ?? [];
    const aggregates = data?.aggregates ?? emptyAggregates;
    const total = data?.total ?? 0;
    // A background refetch keeps the current rows at full opacity.
    const rowsPending = isLoading || (isFetching && isPlaceholderData);

    usePageClamp({
        page: state.page,
        pageSize,
        total: data?.total,
        ready: !isLoading && !isPlaceholderData && !!data,
        setPage: (page) => setUrlState({ page }),
    });

    const onSort = (sort: string, order: 'asc' | 'desc') =>
        setUrlState({ sort: sort as typeof state.sort, order, page: 1 });

    const tiles = [
        { label: 'Requests', value: aggregates.total_requests },
        { label: 'Unique Entries', value: aggregates.unique_count },
        { label: 'Duplicate Entries', value: aggregates.duplicate_count },
        { label: 'In Tokens', value: aggregates.input_tokens_sum },
        { label: 'In Tokens (Converted)', value: aggregates.input_tokens_converted_sum },
    ];

    return (
        <div>
            {error && <ErrorNotice message="Failed to load API usage." />}
            {/* Tab-wide filters: they scope the chart, the tiles, and the table. */}
            <SectionFilterBar>
                <ApiKeyFilter scope={API_SCOPE} value={state.keys} onChange={(keys) => setUrlState({ keys, page: 1 })} className="h-10 text-base" />
                <select
                    aria-label="Status filter"
                    className={filterClass}
                    value={state.status}
                    onChange={(e) => setUrlState({ status: e.target.value as typeof state.status, page: 1 })}
                >
                    <option value="">All Statuses</option>
                    <option value="success">success</option>
                    <option value="error">error</option>
                </select>
                <DateRangeFilter
                    value={rangeFilter}
                    onChange={(next) => setUrlState({ range: next.preset, from: next.from, to: next.to, page: 1 })}
                    className="!h-10"
                />
                <RefreshButton
                    onClick={() => {
                        refetch();
                        refetchDaily();
                    }}
                    fetching={isFetching || isDailyFetching}
                    disabled={!range}
                />
            </SectionFilterBar>
            <div role="group" aria-label="Usage totals" className="grid grid-cols-[repeat(auto-fit,minmax(11rem,1fr))] gap-4 mb-6">
                {tiles.map((tile) => (
                    <StatTile key={tile.label} label={tile.label} value={tile.value.toLocaleString()} />
                ))}
            </div>
            <UsageBreakdownChart rows={chartRows} range={range} countLabel="Requests" tokenKinds={CHART_TOKEN_KINDS} error={!!dailyError} urlPrefix={urlPrefix} />
            <ConvertedTokensChart rows={convertedRows} range={range} error={!!dailyError} />
            <TableCard>
                <TableHeader
                    title="API Key Requests"
                    pagination={{
                        offset,
                        limit: pageSize,
                        total,
                        onOffsetChange: (nextOffset) => setUrlState({ page: nextOffset / pageSize + 1 }),
                        onLimitChange: (size) => {
                            setPageSize(size);
                            setUrlState({ page: 1 });
                        },
                    }}
                />
                <TableViewport className={rowsPending ? 'opacity-50 transition-opacity' : ''}>
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-700">
                                <SortableTh label="API Key" sortKey="api_key" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                                <SortableTh label="Status" sortKey="status" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                                <SortableTh label="In Tokens" sortKey="input_tokens" sort={state.sort} order={state.order} onSort={onSort} />
                                <SortableTh label="In Tokens (Converted)" sortKey="input_tokens_converted" sort={state.sort} order={state.order} onSort={onSort} />
                                <SortableTh label="Cost" sortKey="cost" sort={state.sort} order={state.order} onSort={onSort} />
                                <SortableTh label="Completed At" sortKey="completed_at" sort={state.sort} order={state.order} onSort={onSort} />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                            {requests.map((request) => (
                                <tr
                                    key={request.id}
                                    className="cursor-pointer hover:bg-body-bg/50"
                                    onClick={() => navigate(`/usage/api/requests/${request.id}`, { state: fromHere(location) })}
                                >
                                    <td className={tdClass}>{apiKeyName(request)}</td>
                                    <td className="px-4 py-3 text-sm"><Badge value={request.status} /></td>
                                    <td className={tdClass}>{request.input_tokens_sum.toLocaleString()}</td>
                                    <td className={tdClass}>{request.input_tokens_converted_sum.toLocaleString()}</td>
                                    <td className={tdClass}>{formatCentsAsDollars(request.cost_cents)}</td>
                                    <td className={tdClass}>{formatDateTimeUTC(request.completed_at)}</td>
                                </tr>
                            ))}
                            {requests.length === 0 && (
                                <EmptyRow
                                    colSpan={6}
                                    text={rowsPending ? 'Loading...' : range ? 'No API requests found.' : 'Pick a valid date range.'}
                                />
                            )}
                        </tbody>
                    </table>
                </TableViewport>
            </TableCard>
        </div>
    );
}

export default UsageTab;
